import React, { useState, useMemo } from 'react';
import { 
  X, Search, Filter, ChevronRight, ChevronsRight, ChevronLeft, ChevronsLeft,
  BookOpen, Clock, User, Tags, Save
} from 'lucide-react';

const AssignSubjectsModal = ({
  isOpen,
  onClose,
  classes,
  subjects,
  studentSubjects,
  onSubmit,
  loading
}) => {
  const [classId, setClassId] = useState('');
  const [academicYear, setAcademicYear] = useState(new Date().getFullYear() + '-' + (new Date().getFullYear() + 1));
  const [searchQuery, setSearchQuery] = useState('');
  const [subjectTypeFilter, setSubjectTypeFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  
  // The subjects user wants to assign (right list)
  const [selectedSubjectIds, setSelectedSubjectIds] = useState([]);
  
  // Highlighted items in each list
  const [leftHighlighted, setLeftHighlighted] = useState(new Set());
  const [rightHighlighted, setRightHighlighted] = useState(new Set());

  // Drag and drop state
  const [draggedSubject, setDraggedSubject] = useState(null);
  
  // Preview state
  const [showPreview, setShowPreview] = useState(false);

  // Filter available subjects based on selected class and already assigned
  const classOptions = classes || [];
  const selectedClassObj = classOptions.find(c => c.id == classId);
  
  const alreadyAssignedIds = useMemo(() => {
    if (!selectedClassObj) return [];
    const classData = studentSubjects?.find(cs => cs.className === selectedClassObj.label);
    return classData?.subjects?.map(s => s.subjectId) || [];
  }, [selectedClassObj, studentSubjects]);

  const allAvailableSubjects = useMemo(() => {
    return (subjects || []).filter(subject => 
      subject && subject.subjectId && subject.subjectName && 
      !alreadyAssignedIds.includes(subject.subjectId)
    );
  }, [subjects, alreadyAssignedIds]);

  // Split into left and right lists
  const leftListUnfiltered = allAvailableSubjects.filter(s => !selectedSubjectIds.includes(s.subjectId.toString()));
  const rightList = allAvailableSubjects.filter(s => selectedSubjectIds.includes(s.subjectId.toString()));

  // Filter left list based on search and filters
  const leftList = leftListUnfiltered.filter(s => {
    const matchesSearch = s.subjectName.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          (s.subjectCode || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = !subjectTypeFilter || s.subjectType === subjectTypeFilter;
    const matchesDept = !departmentFilter || s.departmentName === departmentFilter;
    return matchesSearch && matchesType && matchesDept;
  });

  // Transfer handlers
  const moveRight = (e) => {
    e?.preventDefault();
    setSelectedSubjectIds(prev => [...prev, ...Array.from(leftHighlighted)]);
    setLeftHighlighted(new Set());
  };

  const moveAllRight = (e) => {
    e?.preventDefault();
    const allLeftIds = leftList.map(s => s.subjectId.toString());
    setSelectedSubjectIds(prev => [...new Set([...prev, ...allLeftIds])]);
    setLeftHighlighted(new Set());
  };

  const moveLeft = (e) => {
    e?.preventDefault();
    setSelectedSubjectIds(prev => prev.filter(id => !rightHighlighted.has(id)));
    setRightHighlighted(new Set());
  };

  const moveAllLeft = (e) => {
    e?.preventDefault();
    setSelectedSubjectIds([]);
    setRightHighlighted(new Set());
  };

  const toggleHighlight = (id, side) => {
    const setHighlight = side === 'left' ? setLeftHighlighted : setRightHighlighted;
    setHighlight(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleDragStart = (e, subject, sourceSide) => {
    setDraggedSubject({ subject, sourceSide });
    e.dataTransfer.effectAllowed = 'move';
    // Add visual cue
    setTimeout(() => {
      if (e.target) e.target.style.opacity = '0.5';
    }, 0);
  };

  const handleDragEnd = (e) => {
    if (e.target) e.target.style.opacity = '1';
    setDraggedSubject(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetSide) => {
    e.preventDefault();
    if (!draggedSubject) return;
    
    const { subject, sourceSide } = draggedSubject;
    const idStr = subject.subjectId.toString();

    if (sourceSide === 'left' && targetSide === 'right') {
      setSelectedSubjectIds(prev => [...prev, idStr]);
      setLeftHighlighted(prev => {
        const next = new Set(prev);
        next.delete(idStr);
        return next;
      });
    } else if (sourceSide === 'right' && targetSide === 'left') {
      setSelectedSubjectIds(prev => prev.filter(id => id !== idStr));
      setRightHighlighted(prev => {
        const next = new Set(prev);
        next.delete(idStr);
        return next;
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedSubjectIds.length === 0 || !classId || !academicYear) return;
    
    // Parent expects an event and state is updated from SchoolAdmin, 
    // we'll pass fake event and data object
    const fakeEvent = { preventDefault: () => {} };
    onSubmit({
      classId,
      academicYear,
      subjectIds: selectedSubjectIds
    }, fakeEvent);
  };

  if (!isOpen) return null;

  // --- STYLES ---
  const styles = {
    overlay: {
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(15, 23, 42, 0.6)',
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      zIndex: 1000,
      backdropFilter: 'blur(4px)',
      animation: 'fadeIn 0.2s ease-out'
    },
    modal: {
      backgroundColor: '#FFFFFF',
      borderRadius: '20px',
      width: '900px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      overflow: 'hidden',
      animation: 'slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
    },
    header: {
      padding: '24px 32px',
      borderBottom: '1px solid #F1F5F9',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      backgroundColor: '#FFFFFF'
    },
    content: {
      padding: '32px',
      overflowY: 'auto',
      flex: 1,
      backgroundColor: '#F8FAFC'
    },
    footer: {
      padding: '20px 32px',
      borderTop: '1px solid #F1F5F9',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      backgroundColor: '#FFFFFF'
    },
    formRow: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '24px',
      marginBottom: '24px'
    },
    label: {
      display: 'block',
      fontSize: '13px',
      fontWeight: '600',
      color: '#475569',
      marginBottom: '8px'
    },
    input: {
      width: '100%',
      padding: '12px 16px',
      borderRadius: '12px',
      border: '1px solid #E2E8F0',
      fontSize: '14px',
      backgroundColor: '#FFFFFF',
      color: '#0F172A',
      outline: 'none',
      transition: 'border-color 0.2s, box-shadow 0.2s',
      boxSizing: 'border-box'
    },
    select: {
      width: '100%',
      padding: '12px 16px',
      borderRadius: '12px',
      border: '1px solid #E2E8F0',
      fontSize: '14px',
      backgroundColor: '#FFFFFF',
      color: '#0F172A',
      outline: 'none',
      appearance: 'none',
      cursor: 'pointer',
      backgroundImage: 'url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%2364748B%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E")',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'right 16px center',
      backgroundSize: '16px',
      boxSizing: 'border-box'
    },
    dualListContainer: {
      display: 'grid',
      gridTemplateColumns: '1fr 60px 1fr',
      gap: '16px',
      height: '400px'
    },
    listCol: {
      backgroundColor: '#FFFFFF',
      borderRadius: '16px',
      border: '1px solid #E2E8F0',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
    },
    listHeader: {
      padding: '16px',
      borderBottom: '1px solid #E2E8F0',
      backgroundColor: '#F8FAFC',
      fontWeight: '600',
      fontSize: '14px',
      color: '#0F172A',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    listBody: {
      flex: 1,
      overflowY: 'auto',
      padding: '8px'
    },
    subjectCard: (isHighlighted, isDragging) => ({
      padding: '12px 16px',
      borderRadius: '8px',
      border: `1px solid ${isHighlighted ? '#3B82F6' : 'transparent'}`,
      backgroundColor: isHighlighted ? '#EFF6FF' : '#FFFFFF',
      marginBottom: '4px',
      cursor: 'grab',
      transition: 'all 0.15s ease',
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
      opacity: isDragging ? 0.5 : 1
    }),
    controls: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '12px'
    },
    arrowBtn: {
      width: '40px',
      height: '40px',
      borderRadius: '10px',
      border: '1px solid #E2E8F0',
      backgroundColor: '#FFFFFF',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      color: '#475569',
      transition: 'all 0.2s',
      boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
    },
    badge: (type) => ({
      padding: '2px 8px',
      borderRadius: '12px',
      fontSize: '11px',
      fontWeight: '600',
      backgroundColor: type === 'Core' ? '#ECFDF5' : '#EEF2FF',
      color: type === 'Core' ? '#059669' : '#4F46E5',
    })
  };

  return (
    <div style={styles.overlay}>
      <style>{`
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        .transfer-btn:hover:not(:disabled) { background-color: #F8FAFC !important; border-color: #CBD5E1 !important; color: #0F172A !important; }
        .transfer-btn:active:not(:disabled) { transform: scale(0.95); }
        .subject-card:hover { background-color: #F8FAFC; border-color: #E2E8F0 !important; }
        .custom-scroll::-webkit-scrollbar { width: 6px; }
        .custom-scroll::-webkit-scrollbar-track { background: transparent; }
        .custom-scroll::-webkit-scrollbar-thumb { background: #CBD5E1; border-radius: 4px; }
        .custom-scroll::-webkit-scrollbar-thumb:hover { background: #94A3B8; }
      `}</style>
      
      <div style={styles.modal}>
        {/* Header */}
        <div style={styles.header}>
          <div>
            <h2 style={{ margin: '0 0 4px 0', fontSize: '20px', fontWeight: '700', color: '#0F172A' }}>Assign Subjects to Class</h2>
            <p style={{ margin: 0, fontSize: '13px', color: '#64748B' }}>Select a class and assign subjects using the transfer list below.</p>
          </div>
          <button 
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '8px', borderRadius: '50%', color: '#64748B', display: 'flex' }}
            onMouseOver={e => e.currentTarget.style.backgroundColor = '#F1F5F9'}
            onMouseOut={e => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div style={styles.content}>
          {showPreview ? (
            <div style={{ animation: 'fadeIn 0.2s ease-out' }}>
              <div style={{ marginBottom: '24px', padding: '20px', backgroundColor: '#EFF6FF', borderRadius: '12px', border: '1px solid #BFDBFE' }}>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', color: '#1E3A8A', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <BookOpen size={18} /> Assignment Preview
                </h3>
                <p style={{ margin: 0, fontSize: '14px', color: '#3B82F6' }}>
                  You are about to assign <strong>{rightList.length}</strong> new subjects to <strong>{selectedClassObj?.label}</strong> for the academic year <strong>{academicYear}</strong>.
                </p>
              </div>
              <div style={{ border: '1px solid #E2E8F0', borderRadius: '12px', overflow: 'hidden', backgroundColor: '#FFFFFF', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#F8FAFC', borderBottom: '1px solid #E2E8F0' }}>
                      <th style={{ padding: '16px', color: '#475569', fontWeight: '600' }}>Subject Name</th>
                      <th style={{ padding: '16px', color: '#475569', fontWeight: '600' }}>Code</th>
                      <th style={{ padding: '16px', color: '#475569', fontWeight: '600' }}>Type</th>
                      <th style={{ padding: '16px', color: '#475569', fontWeight: '600' }}>Credits</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rightList.map(subject => (
                      <tr key={subject.subjectId} style={{ borderBottom: '1px solid #F1F5F9' }}>
                        <td style={{ padding: '16px', color: '#0F172A', fontWeight: '600' }}>{subject.subjectName}</td>
                        <td style={{ padding: '16px', color: '#64748B' }}>{subject.subjectCode || 'N/A'}</td>
                        <td style={{ padding: '16px' }}><span style={styles.badge(subject.subjectType)}>{subject.subjectType || 'Core'}</span></td>
                        <td style={{ padding: '16px', color: '#64748B' }}>{subject.credits || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <>
              {/* Configuration Row */}
              <div style={styles.formRow}>
            <div>
              <label style={styles.label}>Select Class <span style={{color:'#EF4444'}}>*</span></label>
              <select 
                style={styles.select}
                value={classId}
                onChange={e => {
                  setClassId(e.target.value);
                  setSelectedSubjectIds([]);
                  setLeftHighlighted(new Set());
                  setRightHighlighted(new Set());
                }}
              >
                <option value="">Choose a class...</option>
                {classOptions.map(cls => (
                  <option key={cls.id} value={cls.id}>{cls.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={styles.label}>Academic Year <span style={{color:'#EF4444'}}>*</span></label>
              <input 
                type="text"
                style={styles.input}
                value={academicYear}
                onChange={e => setAcademicYear(e.target.value)}
                placeholder="e.g., 2024-25"
              />
            </div>
          </div>

          {/* Filters Row */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '16px', marginBottom: '24px' }}>
            <div style={{ position: 'relative' }}>
              <Search size={16} color="#94A3B8" style={{ position: 'absolute', left: '14px', top: '14px' }} />
              <input 
                type="text"
                style={{...styles.input, paddingLeft: '40px'}}
                placeholder="Search subjects..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>
            <div style={{ position: 'relative' }}>
              <Filter size={16} color="#94A3B8" style={{ position: 'absolute', left: '14px', top: '14px' }} />
              <select 
                style={{...styles.select, paddingLeft: '40px'}}
                value={subjectTypeFilter}
                onChange={e => setSubjectTypeFilter(e.target.value)}
              >
                <option value="">All Types</option>
                <option value="Core">Core</option>
                <option value="Elective">Elective</option>
                <option value="Practical">Practical</option>
              </select>
            </div>
            <div style={{ position: 'relative' }}>
              <Tags size={16} color="#94A3B8" style={{ position: 'absolute', left: '14px', top: '14px' }} />
              <select 
                style={{...styles.select, paddingLeft: '40px'}}
                value={departmentFilter}
                onChange={e => setDepartmentFilter(e.target.value)}
              >
                <option value="">All Depts</option>
                {/* Dynamically get unique departments */}
                {Array.from(new Set(subjects?.map(s => s?.departmentName).filter(Boolean))).map(dept => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Dual List Container */}
          <div style={styles.dualListContainer}>
            {/* Left List (Available) */}
            <div 
              style={styles.listCol}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, 'left')}
            >
              <div style={styles.listHeader}>
                <span>Available Subjects</span>
                <span style={{ fontSize: '12px', color: '#64748B', fontWeight: '500', backgroundColor: '#F1F5F9', padding: '2px 8px', borderRadius: '12px' }}>
                  {leftList.length}
                </span>
              </div>
              <div className="custom-scroll" style={styles.listBody}>
                {!classId ? (
                  <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#94A3B8', textAlign: 'center', padding: '20px' }}>
                    <BookOpen size={32} style={{ marginBottom: '12px', opacity: 0.5 }} />
                    <p style={{ margin: 0, fontSize: '14px' }}>Please select a class first</p>
                  </div>
                ) : leftList.length === 0 ? (
                  <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94A3B8', fontSize: '14px' }}>
                    No subjects found
                  </div>
                ) : (
                  leftList.map(subject => {
                    const id = subject.subjectId.toString();
                    const isHighlighted = leftHighlighted.has(id);
                    return (
                      <div 
                        key={id} 
                        className="subject-card"
                        style={styles.subjectCard(isHighlighted, draggedSubject?.subject?.subjectId === subject.subjectId)}
                        onClick={() => toggleHighlight(id, 'left')}
                        draggable
                        onDragStart={(e) => handleDragStart(e, subject, 'left')}
                        onDragEnd={handleDragEnd}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <span style={{ fontWeight: '600', color: '#0F172A', fontSize: '14px' }}>{subject.subjectName}</span>
                          <span style={styles.badge(subject.subjectType)}>{subject.subjectType || 'Core'}</span>
                        </div>
                        <div style={{ display: 'flex', gap: '12px', fontSize: '12px', color: '#64748B' }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Tags size={12} /> {subject.subjectCode || 'N/A'}
                          </span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Clock size={12} /> {subject.credits || 0} Credits
                          </span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Controls */}
            <div style={styles.controls}>
              <button className="transfer-btn" style={styles.arrowBtn} onClick={moveRight} disabled={leftHighlighted.size === 0} title="Move Selected Right">
                <ChevronRight size={18} />
              </button>
              <button className="transfer-btn" style={styles.arrowBtn} onClick={moveAllRight} disabled={leftList.length === 0} title="Move All Right">
                <ChevronsRight size={18} />
              </button>
              <button className="transfer-btn" style={styles.arrowBtn} onClick={moveLeft} disabled={rightHighlighted.size === 0} title="Move Selected Left">
                <ChevronLeft size={18} />
              </button>
              <button className="transfer-btn" style={styles.arrowBtn} onClick={moveAllLeft} disabled={rightList.length === 0} title="Move All Left">
                <ChevronsLeft size={18} />
              </button>
            </div>

            {/* Right List (Assigned) */}
            <div 
              style={styles.listCol}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, 'right')}
            >
              <div style={styles.listHeader}>
                <span>Selected to Assign</span>
                <span style={{ fontSize: '12px', color: '#3B82F6', fontWeight: '600', backgroundColor: '#EFF6FF', padding: '2px 8px', borderRadius: '12px' }}>
                  {rightList.length}
                </span>
              </div>
              <div className="custom-scroll" style={styles.listBody}>
                {rightList.length === 0 ? (
                  <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#94A3B8', textAlign: 'center', padding: '20px' }}>
                    <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '12px' }}>
                      <ChevronRight size={24} color="#CBD5E1" />
                    </div>
                    <p style={{ margin: 0, fontSize: '14px' }}>Select subjects from the left and move them here</p>
                  </div>
                ) : (
                  rightList.map(subject => {
                    const id = subject.subjectId.toString();
                    const isHighlighted = rightHighlighted.has(id);
                    return (
                      <div 
                        key={id} 
                        className="subject-card"
                        style={styles.subjectCard(isHighlighted, draggedSubject?.subject?.subjectId === subject.subjectId)}
                        onClick={() => toggleHighlight(id, 'right')}
                        draggable
                        onDragStart={(e) => handleDragStart(e, subject, 'right')}
                        onDragEnd={handleDragEnd}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <span style={{ fontWeight: '600', color: '#0F172A', fontSize: '14px' }}>{subject.subjectName}</span>
                          <span style={styles.badge(subject.subjectType)}>{subject.subjectType || 'Core'}</span>
                        </div>
                        <div style={{ display: 'flex', gap: '12px', fontSize: '12px', color: '#64748B' }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Tags size={12} /> {subject.subjectCode || 'N/A'}
                          </span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
          
          {/* Summary Card */}
          {selectedClassObj && (
            <div style={{ marginTop: '24px', padding: '16px 24px', backgroundColor: '#EFF6FF', borderRadius: '12px', border: '1px solid #BFDBFE', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h4 style={{ margin: '0 0 4px 0', color: '#1E3A8A', fontSize: '14px' }}>Assignment Summary</h4>
                <p style={{ margin: 0, color: '#3B82F6', fontSize: '13px' }}>Class: <strong>{selectedClassObj.label}</strong> • Year: {academicYear}</p>
              </div>
              <div style={{ display: 'flex', gap: '24px' }}>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '12px', color: '#60A5FA', fontWeight: '600', textTransform: 'uppercase' }}>Already Assigned</div>
                  <div style={{ fontSize: '20px', color: '#1E3A8A', fontWeight: '800' }}>{alreadyAssignedIds.length}</div>
                </div>
                <div style={{ width: '1px', backgroundColor: '#BFDBFE' }}></div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '12px', color: '#60A5FA', fontWeight: '600', textTransform: 'uppercase' }}>New Selection</div>
                  <div style={{ fontSize: '20px', color: '#1E3A8A', fontWeight: '800' }}>{rightList.length}</div>
                </div>
              </div>
            </div>
          )}
            </>
          )}

        </div>

        {/* Footer */}
        <div style={styles.footer}>
          {showPreview ? (
            <>
              <button 
                onClick={() => setShowPreview(false)}
                style={{ padding: '10px 20px', borderRadius: '10px', border: '1px solid #E2E8F0', backgroundColor: '#FFFFFF', color: '#475569', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}
              >
                Back to Edit
              </button>
              <button 
                onClick={handleSubmit}
                disabled={loading}
                style={{ padding: '10px 24px', borderRadius: '10px', border: 'none', backgroundColor: '#2563EB', color: '#FFFFFF', fontSize: '14px', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.2)' }}
              >
                <Save size={16} />
                {loading ? 'Saving...' : 'Confirm & Save'}
              </button>
            </>
          ) : (
            <>
              <button 
                onClick={onClose}
                style={{ padding: '10px 20px', borderRadius: '10px', border: '1px solid #E2E8F0', backgroundColor: '#FFFFFF', color: '#475569', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}
              >
                Cancel
              </button>
              
              <div style={{ display: 'flex', gap: '12px' }}>
                <button 
                  onClick={() => setShowPreview(true)}
                  style={{ padding: '10px 20px', borderRadius: '10px', border: '1px solid #E2E8F0', backgroundColor: '#F8FAFC', color: '#0F172A', fontSize: '14px', fontWeight: '600', cursor: selectedSubjectIds.length === 0 ? 'not-allowed' : 'pointer', opacity: selectedSubjectIds.length === 0 ? 0.5 : 1 }}
                  disabled={selectedSubjectIds.length === 0}
                >
                  Preview Assignment
                </button>
                <button 
                  onClick={handleSubmit}
                  disabled={loading || selectedSubjectIds.length === 0 || !classId}
                  style={{ padding: '10px 24px', borderRadius: '10px', border: 'none', backgroundColor: '#2563EB', color: '#FFFFFF', fontSize: '14px', fontWeight: '600', cursor: (loading || selectedSubjectIds.length === 0 || !classId) ? 'not-allowed' : 'pointer', opacity: (loading || selectedSubjectIds.length === 0 || !classId) ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.2)' }}
                >
                  <Save size={16} />
                  {loading ? 'Saving...' : 'Save Assignment'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssignSubjectsModal;
